/* UGC carousel — Model B (transform-only) infinite loop with free drag.
   Position is one unbounded float `x`, wrapped by modulo in the same frame it
   is painted, so the drag never hits the end of a clone strip. */
(function () {
  var SECTION_SELECTOR = '.oddit-section-ugc';
  var CARD_SELECTOR = '.oddit-section-ugc__card';
  var OMEGA = 14; // settle-spring stiffness (rad/s)
  var MIN_FLING = 120; // px/s — below this a release is a stop, not a throw
  var MAX_THROW_CARDS = 3;

  function sanitizeClone(node) {
    node.setAttribute('aria-hidden', 'true');
    node.inert = true;
    var all = [node].concat(Array.prototype.slice.call(node.querySelectorAll('*')));
    all.forEach(function (el) {
      el.removeAttribute('id');
      el.removeAttribute('data-setting-id');
      el.removeAttribute('clip-path');
      Array.prototype.slice.call(el.classList).forEach(function (cls) {
        if (cls.indexOf('oddit-reveal') === 0) el.classList.remove(cls);
      });
      el.style.opacity = '';
      el.style.transform = '';
      el.setAttribute('tabindex', '-1');
    });
    return node;
  }

  function initSection(section) {
    if (section._odditUgcTeardown) section._odditUgcTeardown();

    var track = section.querySelector('.oddit-section-ugc__gallery');
    var inner = section.querySelector('.oddit-section-ugc__track');
    if (!track || !inner) return;

    var reals = Array.prototype.slice.call(inner.querySelectorAll(CARD_SELECTOR));
    var N = reals.length;
    if (N < 2) return;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var controller = new AbortController();
    var listen = { signal: controller.signal };
    var clones = [];

    function addSet(position) {
      var frag = document.createDocumentFragment();
      reals.forEach(function (card) {
        var clone = sanitizeClone(card.cloneNode(true));
        clones.push(clone);
        frag.appendChild(clone);
      });
      if (position === 'lead') inner.insertBefore(frag, inner.firstChild);
      else inner.appendChild(frag);
    }

    // Two sets are needed before the period can be measured from real geometry.
    addSet('tail');

    var setW = 0;
    var pitch = 0;
    var gap = 0;
    var origin = 0;

    function measure() {
      var cards = inner.querySelectorAll(CARD_SELECTOR);
      // Measured, never reconstructed as N * (cardW + gap) — offsetLeft rounds.
      setW = cards[N].getBoundingClientRect().left - cards[0].getBoundingClientRect().left;
      pitch = setW / N;
      gap = pitch - cards[0].getBoundingClientRect().width;
      // Loop period is N * pitch; the ink the cards cover is one gap less.
      origin = (track.clientWidth - (setW - gap)) / 2;
    }

    measure();
    if (!(setW > 0)) return;

    var K = Math.max(3, Math.ceil((track.clientWidth - origin) / setW) + 2);
    addSet('lead');
    for (var s = 3; s < K; s++) addSet('tail');
    measure();

    var x = 0;

    function render() {
      var wrapped = ((x % setW) + setW) % setW;
      inner.style.transform = 'translate3d(' + (origin - wrapped - setW) + 'px,0,0)';
    }

    render();
    // Re-measure after first paint: fonts/images can shift widths.
    var initRaf = requestAnimationFrame(function () {
      initRaf = null;
      measure();
      render();
    });

    // ── settle spring ───────────────────────────────────────────────────
    var settleRaf = null;
    var target = 0;
    var settleVel = 0;
    var lastT = 0;

    function cancelSettle() {
      if (settleRaf) cancelAnimationFrame(settleRaf);
      settleRaf = null;
    }

    function snapTarget(from, v) {
      var throwDist = Math.abs(v) < MIN_FLING ? 0 : v * 0.25;
      var limit = MAX_THROW_CARDS * pitch;
      throwDist = Math.max(-limit, Math.min(limit, throwDist));
      return Math.round((from + throwDist) / pitch) * pitch;
    }

    function step(now) {
      settleRaf = null;
      var dt = Math.min((now - lastT) / 1000, 0.05);
      lastT = now;
      var d0 = x - target;
      var b = settleVel + OMEGA * d0; // b, not the new displacement — else it rings
      var decay = Math.exp(-OMEGA * dt);
      x = target + (d0 + b * dt) * decay;
      settleVel = (settleVel - OMEGA * b * dt) * decay;
      render();
      if (Math.abs(x - target) < 0.1 && Math.abs(settleVel) < 0.1) {
        x = target;
        render();
        return;
      }
      settleRaf = requestAnimationFrame(step);
    }

    function startSettle(v) {
      target = snapTarget(x, v);
      if (reduce) {
        x = target;
        render();
        return;
      }
      settleVel = v;
      lastT = performance.now();
      cancelSettle();
      settleRaf = requestAnimationFrame(step);
    }

    // ── drag ────────────────────────────────────────────────────────────
    var dragging = false;
    var pointerId = null;
    var startClientX = 0;
    var startX = 0;
    var moved = false;
    var samples = [];

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      cancelSettle();
      dragging = true;
      moved = false;
      pointerId = e.pointerId;
      startClientX = e.clientX;
      startX = x;
      samples = [{ t: performance.now(), x: x }];
      track.classList.add('is-dragging');
      try { track.setPointerCapture(e.pointerId); } catch (err) { /* no capture */ }
    }, listen);

    track.addEventListener('pointermove', function (e) {
      if (!dragging || e.pointerId !== pointerId) return;
      var dx = e.clientX - startClientX;
      if (Math.abs(dx) > 4) moved = true;
      x = startX - dx; // absolute from drag origin — no accumulated drift
      samples.push({ t: performance.now(), x: x });
      if (samples.length > 16) samples.shift();
      render();
    }, listen);

    function endDrag(e) {
      if (!dragging || (e && e.pointerId !== pointerId)) return;
      dragging = false;
      pointerId = null;
      track.classList.remove('is-dragging');
      var now = performance.now();
      samples.push({ t: now, x: x }); // stamp at release, else a coast-to-stop flings
      var cutoff = now - 100;
      var recent = samples.filter(function (s) { return s.t >= cutoff; });
      var first = recent.length >= 2 ? recent[0] : samples[0];
      var dt = (now - first.t) / 1000;
      startSettle(dt > 0.008 ? (x - first.x) / dt : 0);
    }

    track.addEventListener('pointerup', endDrag, listen);
    track.addEventListener('pointercancel', endDrag, listen); // browser took the gesture
    track.addEventListener('dragstart', function (e) { e.preventDefault(); }, listen);
    track.addEventListener('click', function (e) {
      if (!moved) return;
      moved = false;
      e.preventDefault();
      e.stopPropagation();
    }, { capture: true, signal: controller.signal });

    // ── wheel / trackpad ────────────────────────────────────────────────
    var wheelIdle = null;

    track.addEventListener('wheel', function (e) {
      var horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      var delta = horizontal ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
      if (!delta) return;

      if (e.deltaMode === 1) delta *= 16;
      else if (e.deltaMode === 2) delta *= track.clientWidth;

      e.preventDefault();
      cancelSettle();
      x += delta;
      render();

      clearTimeout(wheelIdle);
      wheelIdle = setTimeout(function () { startSettle(0); }, 120);
    }, { passive: false, signal: controller.signal });

    // ── resize ──────────────────────────────────────────────────────────
    var lastWidth = window.innerWidth;
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (window.innerWidth === lastWidth) return; // mobile URL-bar height changes
        lastWidth = window.innerWidth;
        initSection(section); // clone count depends on viewport width
      }, 200);
    }, listen);

    section._odditUgcTeardown = function () {
      section._odditUgcTeardown = null;
      controller.abort();
      cancelSettle();
      if (initRaf) cancelAnimationFrame(initRaf);
      clearTimeout(resizeTimer);
      clearTimeout(wheelIdle);
      clones.forEach(function (c) { if (c.parentNode) c.parentNode.removeChild(c); });
      inner.style.transform = '';
      track.classList.remove('is-dragging');
    };
  }

  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section && section._odditUgcTeardown) section._odditUgcTeardown();
  });
})();
